///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthCreateLinkCommand } from '../engine/commands/link/create-link-command';
import { GtthCreateTerminalCommand } from '../engine/commands/node/create-terminal-command';
import { LinkFeature } from './link/link-feature';
import { SectorFeature } from './sector/sector-feature';
import { GtthHistoryService } from '../engine/history-service';
import {
    AutoConnectDTO,
    AutoSetFrequenciesDTO,
    DesignRingsDTO,
    ExtendedProductdata,
    GtthDesignChartData,
    GtthLayerDTO,
    GtthStepGroupEnum,
    HouseDetectionDTO,
    NodeTypeEnum,
    PlannerFeatureCollectionDTO,
    ProductServiceTypeEnum,
    ProjectDesignConfigDTO,
    ProjectSelectionDTO
} from '../../../generated/shared-types.model';
import { _cloneDeep } from '../../../shared/utils';
import { GtthUpdateMainConfigCommand } from '../engine/commands/project/update-main-config-command';
import { GtthNodeFeatureDtoModel } from '../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { GtthLinkFeatureDtoModel } from '../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { NodeFeature } from './node/node-feature';
import { GtthAutoConnectCommand } from '../engine/commands/design/auto-connect-command';
import { GtthHouseDetectionCommand } from '../engine/commands/node/house-detection-command';
import { TopologyLinkTypeEnum } from '../../../services/project-socket/dto/topology-types.model';
import { SelectionService } from '../engine/selection-service';
import { GtthAutoDesignCommand } from '../engine/commands/design/auto-design-command';
import { ProjectDialogComponent } from '../../../projects/dialogs/project-dialog/project-dialog.component';
import { GtthLayerDtoModel } from '../../../services/project-socket/dto/gtth-layer-dto.model';
import { GtthPosition } from './position';
import { DeviceSectorsFeature } from './sector/device-sectors-feature';
import { ExportLinkConfig } from '../../../services/logical-topology/export-link-config.model';
import { ProjectProductsService } from '../engine/project-products-service';
import { AutoDesignPtMpModel } from '../../../services/logical-topology/design/auto-design-ptmp.model';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { SectorsService } from './sector/sectors-service';
import { isDefined } from '@siklu/sui-lib/utils';

export class GtthLogicalTopology {
    public static readonly DEFAULT_CONFIG_LAYER_INDEX = 0;
    public static readonly WHOLE_TOPOLOGY_LAYER_INDEX = -1;

    public layersModeEnabled = false;
    public mhTgBuProductsUsed: boolean;

    public productMaxDistancePerLayer: number[];
    public defaultBuProductPerLayer: number[];
    public defaultTuProductPerLayer: number[];
    public defaultHeightPerLayer: number[];
    public isBlockedByPole: boolean[];

    public locationNamePrefix: string;

    public layers: GtthLayerDtoModel[];
    public exportLinksConfig: ExportLinkConfig;
    public autoConnectOptions: AutoConnectDTO;
    public autodesignP2mpPerLayer: AutoDesignPtMpModel[];
    public designRingsPerLayer: DesignRingsDTO[];
    public autoSetFrequenciesPerLayer: AutoSetFrequenciesDTO[];
    public houseDetectionConfig: HouseDetectionDTO;

    // iterative design
    public selectedDesignIteration = -1;
    public designIterationsChartData: GtthDesignChartData[];
    public selectedServiceTypePerLayer: ProductServiceTypeEnum[] = [];
    public selectedServiceYearsPerLayer: number[] = [];
    public predefined_devices_products: any;
    public productUnits: string;
    public basic_discount = 0;
    public products_discounts: any;
    public isBomStepDisabled: boolean;
    public sectorsService: SectorsService;
    private readonly selectionService: SelectionService;
    private configObjectVersion;
    private tuRequiredRatePerLayer: number[];
    private nodeNamesSectorsToShow: string[];
    private terminalStationsMap: Map<number, NodeFeature>;
    private linksMap: Map<number, LinkFeature>;
    private readonly showDesignSubject = new BehaviorSubject(false);

    constructor() {
        this.terminalStationsMap = new Map();
        this.linksMap = new Map();
        this.selectionService = new SelectionService();
    }

    private _activeLayerIdx: number;

    get activeLayerIdx(): number {
        return this.layersModeEnabled ? this._activeLayerIdx : GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    }

    set activeLayerIdx(value: number) {
        this._activeLayerIdx = value;
    }

    private _selectedProducts: ExtendedProductdata[][];

    get selectedProducts(): ExtendedProductdata[][] {
        return this._selectedProducts;
    }

    set selectedProducts(value: ExtendedProductdata[][]) {
        this._selectedProducts = value;

        this.productMaxDistancePerLayer = [];

        if (this._selectedProducts) {
            this._selectedProducts.forEach((products, index) => {
                let max = 0;

                products.forEach((product) => (max = max < product.threshold ? product.threshold : max));
                this.productMaxDistancePerLayer[index] = max;
            });
        }
    }

    get isShowPlanningMode(): boolean {
        return this.showDesignSubject.getValue();
    }

    set isShowPlanningMode(value: boolean) {
        this.showDesignSubject.next(value);
    }

    private static calculateDistanceBetweenNodes(topologyNode: NodeFeature, movedNodePos: GtthPosition): number {
        const topologyNodePosition = topologyNode.getPosition().toLatLng(google);
        const movedNodePosition = movedNodePos.toLatLng(google);
        return google.maps.geometry.spherical.computeDistanceBetween(topologyNodePosition, movedNodePosition);
    }

    public designStateChanged(): Observable<boolean> {
        return this.showDesignSubject.asObservable().pipe(distinctUntilChanged());
    }

    public completeDesignStateSubject(): void {
        this.showDesignSubject.complete();
    }

    selectedProductsContainsMhTgBu(projectProductsService: ProjectProductsService, layerIndex?: number): boolean {
        const productsList = isDefined(layerIndex) ? this.selectedProducts[layerIndex] : this.selectedProducts.flat();
        return productsList.some((productData) => projectProductsService.isMhTgBuDevice(productData.usedProd));
    }

    getIsIpConfigDefined(): boolean {
        return this.exportLinksConfig.startIp && this.exportLinksConfig.defaultGateway && isDefined(this.exportLinksConfig.subnetMask);
    }

    async setShowPlanningMode(
        historyService: GtthHistoryService,
        value: boolean,
        saveMainConfig: boolean,
        designStarted = false
    ): Promise<any> {
        this.isShowPlanningMode = value;
        historyService.projectUserSessionService.isShowLinksAndVerifiedLoses = designStarted ? false : !this.isShowPlanningMode;

        this.redrawTopologyLines();

        this.mhTgBuProductsUsed = false;

        if (this.isShowPlanningMode) {
            await historyService.bomReportService.updateBom(true);
            for (const link of this.getLinksIterator()) {
                if (link.isActive() && historyService.projectProductsService.isMhTgBuDevice(link.getBuUsedProduct())) {
                    this.mhTgBuProductsUsed = true;
                    break;
                }
            }
        } else {
            historyService.interactiveWindowsService.closeAll();
        }

        this.redrawSectors(historyService);
        this.stationsChangeDraggable();
        this.changeStationIcons();

        historyService.projectInfoViewService.setNumberOfConnectedTu(this.getConnectedTerminalStationsCount());

        let result = true;
        if (saveMainConfig) {
            const updateMainCommand = historyService.command(GtthUpdateMainConfigCommand);
            result = await historyService.doCommandBypassHistory(updateMainCommand);
        }

        return result;
    }

    parseGeoJsonMainConfig(geoJson: PlannerFeatureCollectionDTO, historyService: GtthHistoryService) {
        const properties = geoJson.crs.properties;

        this.configObjectVersion = properties.configObjectVersion;

        this.parseLayers(properties);

        this._activeLayerIdx = properties.activeLayerIdx;
        this.tuRequiredRatePerLayer = properties.tuRequiredRatePerLayer;
        this.defaultBuProductPerLayer = properties.defaultBuProductPerLayer;
        this.defaultTuProductPerLayer = properties.defaultTuProductPerLayer;
        this.defaultHeightPerLayer = properties.defaultHeightPerLayer;
        this.exportLinksConfig = new ExportLinkConfig(properties.saved_exportLinksConfig);

        this.autoConnectOptions = this.getArrayElementOrDefault(
            properties.autoconnectPerLayer,
            GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX,
            GtthAutoConnectCommand.createDTO()
        );

        this.designRingsPerLayer = this.getArrayOrDefault(properties.designRingsPerLayer, [GtthAutoDesignCommand.createDesignRindsDTO()]);

        this.selectedProducts = properties.selectedProductsPerLayer;

        this.autodesignP2mpPerLayer = this.getAutodesignP2mpPerLayer(properties, historyService);

        this.autoSetFrequenciesPerLayer = this.getArrayOrDefault(properties.autoSetFrequenciesPerLayer, [
            GtthAutoDesignCommand.createAutoSetFrequenciesDTO()
        ]);

        this.selectedServiceTypePerLayer = this.getArrayOrDefault(properties.selectedServiceTypePerLayer, [
            ProductServiceTypeEnum.PLAN_PRO
        ]);

        this.selectedServiceYearsPerLayer = this.getArrayOrDefault(properties.selectedServiceYearsPerLayer, [3]);

        this.layersModeEnabled = properties.layersModeEnabled;
        this.processLayersModeEnabled(properties, historyService);

        this.designIterationsChartData = properties.designIterationsChartData;
        this.selectedDesignIteration = properties.selectedDesignIteration;

        this.basic_discount = this.getPropertyOrDefault(properties.basic_discount, 0);
        this.products_discounts = properties.products_discounts || {};
        this.isShowPlanningMode = properties.isShowPlanningMode;
        this.isBomStepDisabled = properties.bomStepDisabled;
        this.productUnits = properties.productUnits || 'meter';

        this.predefined_devices_products = properties.predefined_devices_products;
        this.houseDetectionConfig = GtthHouseDetectionCommand.createDTO(properties.houseDetectionConfig);
        this.nodeNamesSectorsToShow = this.getPropertyOrDefault(properties.nodeNamesSectorsToShow, []);

        this.locationNamePrefix = properties.locationNamePrefix;

        this.isBlockedByPole = this.getIsBlockedByPole(properties);

        if (properties.designError) {
            historyService.progressWindowService.handleDesignError(properties.designError);
        }
    }

    parseGeoJsonEntities(nodes: GtthNodeFeatureDtoModel[], links: GtthLinkFeatureDtoModel[], historyService: GtthHistoryService) {
        this.terminalStationsMap = new Map();
        this.linksMap = new Map();
        this.sectorsService = new SectorsService();
        this.selectionService.clear();
        nodes ??= [];
        nodes.forEach((feature: GtthNodeFeatureDtoModel) => {
            const createTerminalCommand = historyService.command(GtthCreateTerminalCommand).createFromGeoJson(feature);
            historyService.doCommandBypassHistory(createTerminalCommand, true);
        });

        links ??= [];
        links.forEach((feature: GtthLinkFeatureDtoModel) => {
            const createLinkCommand = historyService.command(GtthCreateLinkCommand).createFromGeoJson(feature);
            historyService.doCommandBypassHistory(createLinkCommand, true);
        });
    }

    clearTopologyEntities(): void {
        if (this.terminalStationsMap) {
            this.terminalStationsMap.forEach((station: NodeFeature) => station.hide());
            this.terminalStationsMap.clear();
        }

        if (this.linksMap) {
            this.linksMap.forEach((link: LinkFeature) => link.hide());
            this.linksMap.clear();
        }

        this.sectorsService?.hideAndClear();
        this.selectionService.clear();
    }

    isTopologyEmpty(): boolean {
        return this.terminalStationsMap.size === 0;
    }

    toGeoJsonMainConfig(historyService: GtthHistoryService, projectName: string): PlannerFeatureCollectionDTO {
        const center = historyService.map.getCenter();

        this.houseDetectionConfig.polygons = undefined;

        return {
            name: projectName,
            bbox: [center.lng(), center.lat(), historyService.map.getZoom()],
            crs: {
                type: 'name',
                properties: {
                    configObjectVersion: this.configObjectVersion,
                    activeLayerIdx: this._activeLayerIdx,
                    layersModeEnabled: this.layersModeEnabled,
                    tuRequiredRatePerLayer: this.tuRequiredRatePerLayer,
                    defaultBuProductPerLayer: this.defaultBuProductPerLayer,
                    defaultTuProductPerLayer: this.defaultTuProductPerLayer,
                    defaultHeightPerLayer: this.defaultHeightPerLayer,
                    saved_exportLinksConfig: this.exportLinksConfig,
                    autoconnectPerLayer: [this.autoConnectOptions],
                    autodesignP2mpPerLayer: this.autodesignP2mpPerLayer,
                    designRingsPerLayer: this.designRingsPerLayer,
                    autoSetFrequenciesPerLayer: this.autoSetFrequenciesPerLayer,
                    designIterationsChartData: this.designIterationsChartData,
                    selectedDesignIteration: this.selectedDesignIteration,
                    basic_discount: this.basic_discount,
                    products_discounts: this.products_discounts,
                    isShowPlanningMode: this.isShowPlanningMode,
                    layers: this.layers,
                    bomStepDisabled: this.isBomStepDisabled,
                    selectedServiceTypePerLayer: this.selectedServiceTypePerLayer,
                    selectedServiceYearsPerLayer: this.selectedServiceYearsPerLayer,
                    productUnits: this.productUnits,
                    selectedProductsPerLayer: this.selectedProducts,
                    predefined_devices_products: this.predefined_devices_products,
                    houseDetectionConfig: this.houseDetectionConfig,
                    storedPolygons: historyService.polygonsService.storedPolygonsToJSON(),
                    nodeNamesSectorsToShow: this.nodeNamesSectorsToShow,
                    designError: undefined,
                    nodesCountPerLayer: this.getNodesPerLayerCount(),
                    isBlockedByPole: this.isBlockedByPole,
                    locationNamePrefix: this.locationNamePrefix
                }
            }
        };
    }

    public getNodesPerLayerCount(calculateAll = true): number[] {
        const nodesCountPerLayer = [];
        this.terminalStationsMap.forEach((terminal: NodeFeature) => {
            if (calculateAll || terminal.isEnabled()) {
                if (!nodesCountPerLayer[terminal.getLayerIndex()]) {
                    nodesCountPerLayer[terminal.getLayerIndex()] = 1;
                } else {
                    nodesCountPerLayer[terminal.getLayerIndex()]++;
                }
            }
        });
        return nodesCountPerLayer;
    }

    public getHasNodesMappedToLayers(): boolean[] {
        let hasNodes;
        if (this.layersModeEnabled && this.layers?.length) {
            hasNodes = [false];
            this.getNodesPerLayerCount().forEach((value: number, index: number) => {
                hasNodes[index] = value > 0;
            });
        } else {
            hasNodes = [true];
        }
        return hasNodes;
    }

    getDesignConfig(): ProjectDesignConfigDTO {
        return {
            autoDesignP2mpConfig: this.autodesignP2mpPerLayer,
            autoSetFrequenciesConfig: this.autoSetFrequenciesPerLayer,
            designRingsConfig: this.designRingsPerLayer
        } as unknown as ProjectDesignConfigDTO;
    }

    enableLayersMode(historyService: GtthHistoryService): void {
        if (!this.layers || this.layers.length === 0) {
            this.layers = [new GtthLayerDtoModel(0, ProjectDialogComponent.DEFAULT_LAYER_NAMES[0])];
        }

        const layerNamesLength = ProjectDialogComponent.DEFAULT_LAYER_NAMES.length;
        const newSelectedProducts = [_cloneDeep(this.selectedProducts[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX])];

        for (let i = 1; i < layerNamesLength; i++) {
            this.copyValueFromDefaultIndex(this.tuRequiredRatePerLayer, i);
            this.copyValueFromDefaultIndex(this.defaultHeightPerLayer, i);
            this.copyValueFromDefaultIndex(this.defaultTuProductPerLayer, i);
            this.copyValueFromDefaultIndex(this.selectedServiceTypePerLayer, i);
            this.copyValueFromDefaultIndex(this.selectedServiceYearsPerLayer, i);
            this.copyValueFromDefaultIndex(this.defaultBuProductPerLayer, i);
            this.copyValueFromDefaultIndex(this.isBlockedByPole, i);

            if (this.selectedProducts.length < i + 1) {
                newSelectedProducts[i] = _cloneDeep(this.selectedProducts[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX]);
            } else {
                newSelectedProducts[i] = this.selectedProducts[i];
            }

            this.extendArrayWithFunction(this.designRingsPerLayer, i + 1, () => GtthAutoDesignCommand.createDesignRindsDTO());
            this.updateAutoDesignP2mpPerLayer(i, historyService);
            this.extendArrayWithFunction(this.autoSetFrequenciesPerLayer, i + 1, () => GtthAutoDesignCommand.createAutoSetFrequenciesDTO());
        }

        this.extendArrayWithFunction(
            this.layers,
            layerNamesLength,
            (index) => new GtthLayerDtoModel(index, ProjectDialogComponent.DEFAULT_LAYER_NAMES[index])
        );

        this.selectedProducts = newSelectedProducts;

        this.layersModeEnabled = true;
        this.activeLayerIdx = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    }

    getMapBounds(): Promise<any> {
        return new Promise((resolve) => {
            let minLat;
            let maxLat;
            let minLong;
            let maxLong;

            const calculate = (latitude: number, longtitude: number) => {
                minLat = isDefined(minLat) ? Math.min(minLat, latitude) : latitude;
                maxLat = isDefined(maxLat) ? Math.max(maxLat, latitude) : latitude;
                minLong = isDefined(minLong) ? Math.min(minLong, longtitude) : longtitude;
                maxLong = isDefined(maxLong) ? Math.max(maxLong, longtitude) : longtitude;
            };

            this.terminalStationsMap.forEach((station: NodeFeature) =>
                calculate(station.getPosition().latitude, station.getPosition().longitude)
            );

            const bounds = new google.maps.LatLngBounds(
                {
                    lat: maxLat,
                    lng: minLong
                },
                { lat: minLat, lng: maxLong }
            );

            resolve(bounds);
        });
    }

    addSelectedStation(station: NodeFeature): void {
        this.selectionService.addStation(station);
    }

    addSelectedStationByName(stationName: string): void {
        const node = this.getStationByName(stationName);
        if (node) {
            this.addSelectedStation(node);
        }
    }

    addSelectedLink(link: LinkFeature): void {
        this.selectionService.addLink(link);
    }

    addSelectedLinkById(linkId: number): void {
        const link = this.getLink(linkId);
        if (link) {
            this.addSelectedLink(link);
        }
    }

    addSelectedSector(sector: SectorFeature): void {
        this.clearSelectedSectors();
        this.selectionService.addSector(sector);
    }

    removeStationFromSelection(stationId: number): void {
        this.selectionService.removeStation(stationId);
    }

    removeLinkFromSelection(linkId: number): void {
        this.selectionService.removeLink(linkId);
    }

    removeSelectedSector(aggregatedSectorIndex: number): void {
        this.selectionService.removeSector(aggregatedSectorIndex);
    }

    hasSelectedEntities(): boolean {
        return this.selectionService.hasSelectedLinks() || this.selectionService.hasSelectedStations();
    }

    hasSelectedLinks(): boolean {
        return this.selectionService.hasSelectedLinks();
    }

    hasSelectedStations(): boolean {
        return this.selectionService.hasSelectedStations();
    }

    getSelectedEntities(): ProjectSelectionDTO {
        return this.selectionService.getSelectedEntities();
    }

    getSelectedStations(): IterableIterator<NodeFeature> {
        return this.selectionService.getStations();
    }

    getSelectedStationsCount(): number {
        return this.selectionService.getStationsCount();
    }

    getSelectedLinks(): IterableIterator<LinkFeature> {
        return this.selectionService.getLinks();
    }

    getSelectedLinksCount(): number {
        return this.selectionService.getLinksCount();
    }

    hasSelectedNotFrozenEntities(): boolean {
        return this.selectionService.hasSelectedNotFrozenEntities();
    }

    getSelectedNotFrozenLinks(): number[] {
        return this.selectionService.getSelectedNotFrozenLinks();
    }

    getSelectedNotFrozenStations(): number[] {
        return this.selectionService.getSelectedNotFrozenStations();
    }

    clearSelection(): void {
        this.selectionService.clearSelection();
    }

    clearSelectedSectors(): void {
        this.selectionService.clearSectorSelection();
    }

    getAllowLosesForSelectedEntities(): number[][] {
        const losList = [];
        for (const station of this.selectionService.getStations()) {
            // all selected to all selected matrix

            this.getAllowStationsForLoses(station.getId()).forEach((stationId: number) => {
                if (!this.selectionService.hasStation(stationId)) {
                    return;
                }
                const hasLos = losList.find((el) => {
                    return el[0] === stationId && el[1] === station.getId();
                });
                if (!hasLos) {
                    losList.push([station.getId(), stationId]);
                }
            });
        }

        return losList;
    }

    getAllowStationsForLoses(stationId: number): number[] {
        const stationsList = [];
        const currentStation = this.getStation(stationId);

        if (!currentStation.isEnabled()) {
            return [];
        }

        const currentStationPosition = new google.maps.LatLng(
            currentStation.getPosition().latitude,
            currentStation.getPosition().longitude
        );

        const neighboursNumber = this.getAutoConnectNumNeighbours();

        if (currentStation.linkIds.size >= neighboursNumber) {
            return [];
        }

        const losExist = this.getExistingLosConnections(stationId);

        const stations = this.getStationsWithinAvailability(currentStation, currentStationPosition);

        stations.sort((a, b) => a.distance - b.distance);

        this.addEligibleStationsToList(stations, currentStation, losExist, stationsList, neighboursNumber);

        return stationsList;
    }

    getExistLosForSelectedEntities(): Set<number> {
        const losList = new Set<number>();

        for (const station of this.selectionService.getStations()) {
            station.linkIds.forEach((el) => {
                const link = this.getLink(el);
                if (link.getLinkType() !== TopologyLinkTypeEnum.NOT_CONFIRMED_LOS) {
                    return;
                }
                if (
                    this.selectionService.hasStation(link.getToStation().getId()) &&
                    this.selectionService.hasStation(link.getFromStation().getId())
                ) {
                    losList.add(el);
                }
            });
        }

        for (const link of this.selectionService.getLinks()) {
            if (this.getLink(link.getId()).getLinkType() !== TopologyLinkTypeEnum.NOT_CONFIRMED_LOS) {
                losList.add(link.getId());
            }
        }

        return losList;
    }

    enableEntitiesListeners(): void {
        if (this.terminalStationsMap) {
            this.terminalStationsMap.forEach((station: NodeFeature) => {
                station.enableListeners();
            });
        }
    }

    disableEntitiesListeners(): void {
        if (this.terminalStationsMap) {
            this.terminalStationsMap.forEach((station: NodeFeature) => {
                station.disableListeners();
            });
        }
    }

    hasStationsWithLayers(): boolean {
        for (const station of this.getStationsIterator()) {
            if (station.getLayerIndex() !== GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX) {
                return true;
            }
        }
        return false;
    }

    getStationsIterator(): IterableIterator<NodeFeature> {
        return this.terminalStationsMap.values();
    }

    getStationByName(stationName: string): NodeFeature {
        for (const [_key, station] of this.terminalStationsMap) {
            if (station.getName() === stationName) {
                return station;
            }
        }
        return undefined;
    }

    getStation(stationId: number): NodeFeature {
        return this.terminalStationsMap.get(stationId);
    }

    addTerminalStation(station: NodeFeature): void {
        this.terminalStationsMap.set(station.getId(), station);
    }

    removeTerminalStation(stationId: number): void {
        this.removeStationFromSelection(stationId);
        this.terminalStationsMap.delete(stationId);
    }

    containsTerminalStation(stationId: number): boolean {
        return this.terminalStationsMap.has(stationId);
    }

    getLink(linkId: number): LinkFeature {
        return this.linksMap.get(linkId);
    }

    getLinkByNodeNames(nodeName1: string, nodeName2: string): LinkFeature {
        const node1 = this.getStationByName(nodeName1);
        const node2 = this.getStationByName(nodeName2);

        if (!node1 || !node2) {
            return undefined;
        }

        return this.getLinkByNodeIds(node1.getId(), node2.getId());
    }

    getLinkByNodeIds(node0Id: number, node1Id: number): LinkFeature {
        const linkId = LinkFeature.generateLinkId(node0Id, node1Id);
        return this.getLink(linkId);
    }

    getLinksIterator(): IterableIterator<LinkFeature> {
        return this.linksMap.values();
    }

    getAllStationLinks(stationId: number): LinkFeature[] {
        const links = [];

        this.linksMap.forEach((link: LinkFeature) => {
            if (link.belongToStation(stationId)) {
                links.push(link);
            }
        });

        return links;
    }

    addLink(link: LinkFeature): void {
        this.linksMap.set(link.getId(), link);
    }

    removeLink(linkId: number): void {
        this.removeLinkFromSelection(linkId);
        this.linksMap.delete(linkId);
    }

    containsLinkByStationsId(station1Id: number, station2Id: number): boolean {
        for (const [_key, link] of this.linksMap) {
            if (link.belongToStation(station1Id) && link.belongToStation(station2Id)) {
                return true;
            }
        }
        return false;
    }

    getTerminalStationsCount(): number {
        return this.terminalStationsMap.size;
    }

    getConnectedTerminalStationsCount(): number {
        if (!this.terminalStationsMap || this.terminalStationsMap.size === 0 || !this.isShowPlanningMode) {
            return 0;
        } else {
            let counter = 0;
            this.terminalStationsMap.forEach((station: NodeFeature) => {
                if (
                    station.isEnabled() &&
                    (this.activeLayerIdx === -1 || station.getLayerIndex() === this.activeLayerIdx) &&
                    (station.getMhType() !== NodeTypeEnum.OPTIONAL_BU || this.hasLinksOrFibers(station.getId()))
                ) {
                    counter++;
                }
            });
            return counter;
        }
    }

    getAllStationsNames(): string[] {
        const namesList = [];

        this.terminalStationsMap.forEach((station: NodeFeature) => {
            namesList.push(station.getName());
        });
        return namesList;
    }

    //     for BS and TS
    hasLinks(stationId: number): boolean {
        const station = this.getStation(stationId);
        if (station?.linkIds) {
            for (const linkId of station.linkIds) {
                const link = this.getLink(linkId);
                if (link.getLinkType() === TopologyLinkTypeEnum.LINK) {
                    return true;
                }
            }
        }

        return false;
    }

    hasLinksOrFibers(stationId: number): boolean {
        const station = this.getStation(stationId);
        if (station?.linkIds) {
            for (const linkId of station.linkIds) {
                const link = this.getLink(linkId);
                if (link.getLinkType() === TopologyLinkTypeEnum.LINK || link.getLinkType() === TopologyLinkTypeEnum.FIBER) {
                    return true;
                }
            }
        }

        return false;
    }

    addLineToStation(station: NodeFeature, linkId: number) {
        if (!station.linkIds.has(linkId)) {
            station.linkIds.add(linkId);
            station.changeDraggable();
        }
    }

    removeLineFromStation(station: NodeFeature, linkId: number) {
        if (station.linkIds.has(linkId)) {
            station.linkIds.delete(linkId);
            station.changeDraggable();
        }
    }

    addLineToStationById(stationId: number, linkId: number) {
        if (this.terminalStationsMap.has(stationId)) {
            this.addLineToStation(this.terminalStationsMap.get(stationId), linkId);
        }
    }

    removeLineFromStationById(stationId: number, linkId: number) {
        if (this.terminalStationsMap.has(stationId)) {
            this.removeLineFromStation(this.terminalStationsMap.get(stationId), linkId);
        }
    }

    getAutoConnectNumNeighbours(): number {
        if (!this.autoConnectOptions.isNumNodesChanged) {
            const nodesCounter = this.getTerminalStationsCount();

            let maxNeighboursValue: number;
            if (nodesCounter < 10) {
                maxNeighboursValue = 5;
            } else if (nodesCounter >= 10 && nodesCounter < 30) {
                maxNeighboursValue = Math.round(nodesCounter / 2);
            } else {
                maxNeighboursValue = 15;
            }
            return maxNeighboursValue;
        } else {
            return this.autoConnectOptions.num_nodes;
        }
    }

    getExistLosForStation(stationId: number): number[] {
        const station = this.getStation(stationId);
        const losList = [];
        station.linkIds.forEach((el: number) => {
            if (this.getLink(el).getLinkType() === TopologyLinkTypeEnum.NOT_CONFIRMED_LOS) {
                losList.push(el);
            }
        });
        return losList;
    }

    getNodeNamesSectorsToShow(): string[] {
        return this.nodeNamesSectorsToShow;
    }

    isHasNodeNamesSectorsToShow(stationName: string): boolean {
        return this.nodeNamesSectorsToShow.indexOf(stationName) !== -1;
    }

    showNodeSectors(stationName: string): void {
        if (this.nodeNamesSectorsToShow.indexOf(stationName) === -1) {
            this.nodeNamesSectorsToShow.push(stationName);
        }
        const station = this.getStationByName(stationName);
        this.sectorsService.getStationSectors(station.getId()).forEach((deviceSector: DeviceSectorsFeature) => {
            deviceSector.draw(false);
        });
    }

    hideNodeSectors(stationName: string): void {
        const stationIndex = this.nodeNamesSectorsToShow.indexOf(stationName);
        if (stationIndex !== -1) {
            this.nodeNamesSectorsToShow.splice(stationIndex, 1);
        }
        const station = this.getStationByName(stationName);
        this.sectorsService.getStationSectors(station.getId()).forEach((deviceSector: DeviceSectorsFeature) => {
            deviceSector.hide();
        });
    }

    clearNodeNamesSectorsToShow(): void {
        this.nodeNamesSectorsToShow = [];
    }

    changeStationIcons(): void {
        this.terminalStationsMap.forEach((station: NodeFeature) => {
            station.refreshState();
        });
    }

    redrawTopologyLines(): void {
        this.linksMap.forEach((link: LinkFeature) => this.redrawLine(link));
    }

    changeSectorsLinksColor(): void {
        this.linksMap.forEach((link: LinkFeature) => link.refreshColor());
        this.sectorsService.refreshColor();
    }

    redrawLine(link: LinkFeature): void {
        if (this.isShowLinkInCurrentState(link)) {
            if (link.draw()) {
                link.initListeners();
            } else {
                link.updateLinkStatus();
            }
        } else {
            link.hide();
        }
    }

    redrawSectors(historyService: GtthHistoryService): void {
        this.sectorsService.redrawSectors(historyService);
    }

    updateLinkSector(link: LinkFeature): void {
        const deviceSectors = this.sectorsService.getDeviceSectors(link.getP2mpRepSectorIndex());
        if (deviceSectors) {
            const sectorInDevice = deviceSectors.getDeviceSector(link.getSectorInDevice());

            deviceSectors.linkFreezeChanged(link);
            sectorInDevice.getLinks().forEach((sectorLink: LinkFeature) => this.redrawLine(sectorLink));
        }
    }

    stationsChangeDraggable(): void {
        this.terminalStationsMap.forEach((station: NodeFeature) => station.changeDraggable());
    }

    isShowLinkInCurrentState(link: LinkFeature): boolean {
        const linkType = link.getLinkType();

        if (link.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES) {
            return true;
        } else if (link.getLinkIsDisabled() || linkType === TopologyLinkTypeEnum.NO_LOS) {
            return false;
        }

        return (
            linkType === TopologyLinkTypeEnum.FIBER ||
            linkType === TopologyLinkTypeEnum.LINK ||
            link.historyService.projectUserSessionService.isShowLinksAndVerifiedLoses
        );
    }

    clearDesignIterationInfo(): void {
        this.selectedDesignIteration = -1;
        this.designIterationsChartData = [];
    }

    hasPopStationsInTopmostLayer(): boolean {
        const popPresentInLayer: boolean[] = [];

        for (const terminal of this.terminalStationsMap.values()) {
            if (terminal.isEnabled()) {
                popPresentInLayer[terminal.getLayerIndex()] ||= terminal.isPop();
            }
        }

        return popPresentInLayer[popPresentInLayer.length - 1];
    }

    getActiveLayerHeight(): number {
        if (this._activeLayerIdx < GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX) {
            return this.defaultHeightPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX];
        } else {
            return this.defaultHeightPerLayer[this._activeLayerIdx];
        }
    }

    checkNodeNewPosition(newNodeId: number, newPosition: GtthPosition): void {
        const stationsIterator = this.getStationsIterator();
        const newStationName = this.getStation(newNodeId).getName();
        for (const station of stationsIterator) {
            if (
                station.getId() !== newNodeId &&
                GtthLogicalTopology.calculateDistanceBetweenNodes(station, newPosition) <= this.houseDetectionConfig.duplicateDistance
            ) {
                const nodeName = station.getName();
                station.historyService.notificationService.showWarning(
                    `"${newStationName}" is too close to "${nodeName}", distance less than ${this.houseDetectionConfig.duplicateDistance} meters`
                );
                return;
            }
        }
    }

    getDeviceSectors(sectorId: number): DeviceSectorsFeature {
        return this.sectorsService.getDeviceSectors(sectorId);
    }

    getSector(sectorId: number, sectorInDevice: number): SectorFeature {
        return this.sectorsService.getSector(sectorId, sectorInDevice);
    }

    private getExistingLosConnections(stationId: number): number[] {
        const losExist = [];
        const currentStation = this.getStation(stationId);

        currentStation.linkIds.forEach((los: number) => {
            const currentLink = this.getLink(los);
            if (currentLink.isFromStation(stationId)) {
                losExist.push(currentLink.getToStation().getId());
            } else if (currentLink.isToStation(stationId)) {
                losExist.push(currentLink.getFromStation().getId());
            }
        });

        return losExist;
    }

    private getStationsWithinAvailability(
        currentStation: NodeFeature,
        currentStationPosition: google.maps.LatLng
    ): {
        id: number;
        distance: number;
    }[] {
        const stations = [];

        this.terminalStationsMap.forEach((station: NodeFeature) => {
            const googleLatLng = new google.maps.LatLng(station.getPosition().latitude, station.getPosition().longitude);
            const distance = google.maps.geometry.spherical.computeDistanceBetween(currentStationPosition, googleLatLng);
            const losLayerIndex = Math.min(station.getLayerIndex(), currentStation.getLayerIndex());
            const currentStationAvailability = this.productMaxDistancePerLayer[losLayerIndex];
            if (distance <= currentStationAvailability) {
                stations.push({ id: station.getId(), distance });
            }
        });

        return stations;
    }

    private addEligibleStationsToList(
        stations: { id: number; distance: number }[],
        currentStation: NodeFeature,
        losExist: number[],
        stationsList: number[],
        neighboursNumber: number
    ): void {
        for (const temp of stations) {
            const stationId = temp.id;

            if (
                stationId === currentStation.getId() ||
                losExist.includes(stationId) ||
                stationsList.length + currentStation.linkIds.size >= neighboursNumber
            ) {
                continue;
            }

            const station = this.getStation(stationId);

            if (!station.isEnabled()) {
                continue;
            }

            stationsList.push(stationId);
        }
    }

    private copyValueFromDefaultIndex<T>(array: T[], index: number): void {
        if (array.length < index + 1) {
            array[index] = array[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX];
        }
    }

    private extendArrayWithFunction<T>(array: T[], targetLength: number, valueGenerator: (index: number) => T): T[] {
        for (let i = array.length; i < targetLength; i++) {
            array[i] = valueGenerator(i);
        }
        return array;
    }

    private updateAutoDesignP2mpPerLayer(index: number, historyService: GtthHistoryService): void {
        if (this.autodesignP2mpPerLayer.length < index + 1) {
            const layerContainsMhTg = this.selectedProductsContainsMhTgBu(historyService.projectProductsService, index);
            this.autodesignP2mpPerLayer[index] = AutoDesignPtMpModel.createAutoDesignPtMpModel(undefined, layerContainsMhTg);
        }
    }

    private parseLayers(properties: any): void {
        this.layers = [];
        if (properties.layers?.length) {
            this.layers = properties.layers.map((layer: GtthLayerDTO, index: number) => {
                return new GtthLayerDtoModel(index, layer.layerName);
            });
        }
    }

    private getArrayElementOrDefault<T>(array: T[] | undefined, index: number, defaultValue: T): T {
        return array && array.length > index && array[index] !== undefined ? array[index] : defaultValue;
    }

    private getArrayOrDefault<T>(array: T[] | undefined, defaultValue: T[]): T[] {
        return array && array.length > 0 ? array : defaultValue;
    }

    private getPropertyOrDefault<T>(property: T | undefined | null, defaultValue: T): T {
        return property ?? defaultValue;
    }

    private getAutodesignP2mpPerLayer(properties: any, historyService: GtthHistoryService): AutoDesignPtMpModel[] {
        if (properties.autodesignP2mpPerLayer?.length > 0) {
            return properties.autodesignP2mpPerLayer.map((data: any, index: number) => {
                const layerContainsMhTg = this.selectedProductsContainsMhTgBu(historyService.projectProductsService, index);
                return AutoDesignPtMpModel.createAutoDesignPtMpModel(data as AutoDesignPtMpModel, layerContainsMhTg);
            });
        } else {
            const layerContainsMhTg = this.selectedProductsContainsMhTgBu(
                historyService.projectProductsService,
                GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
            );
            return [AutoDesignPtMpModel.createAutoDesignPtMpModel(undefined, layerContainsMhTg)];
        }
    }

    private processLayersModeEnabled(properties: any, historyService: GtthHistoryService): void {
        if (!this.layersModeEnabled) {
            return;
        }

        this.layers.forEach((_value, index) => {
            this.designRingsPerLayer[index] = GtthAutoDesignCommand.createDesignRindsDTO(this.designRingsPerLayer[index]);
            const layerContainsMhTg = this.selectedProductsContainsMhTgBu(historyService.projectProductsService, index);
            this.autodesignP2mpPerLayer[index] = AutoDesignPtMpModel.createAutoDesignPtMpModel(
                this.autodesignP2mpPerLayer[index],
                layerContainsMhTg
            );
            this.autoSetFrequenciesPerLayer[index] = GtthAutoDesignCommand.createAutoSetFrequenciesDTO(
                this.autoSetFrequenciesPerLayer[index]
            );

            this.selectedServiceTypePerLayer[index] = this.getPropertyOrDefault(
                properties.selectedServiceTypePerLayer?.[index],
                ProductServiceTypeEnum.PLAN_PRO
            );

            this.selectedServiceYearsPerLayer[index] = this.getPropertyOrDefault(properties.selectedServiceYearsPerLayer?.[index], 3);
        });
    }

    private getIsBlockedByPole(properties: any): boolean[] {
        if (isDefined(properties.isBlockedByPole)) {
            return properties.isBlockedByPole;
        } else {
            return this.layers.map(() => false);
        }
    }
}
